/**
 * Keycloak Callback Page
 * Handles the OAuth 2.0 authorization code flow callback from Keycloak
 */

import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, Card, CardContent, Button, CircularProgress, Typography, Alert } from '@mui/material';
import { apiPost } from '../utils/apiClient';
import logger from '../utils/logger';

const KeycloakCallback = ({ onKeycloakLogin }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [error, setError] = useState(null);

  useEffect(() => {
    const handleCallback = async () => {
      const code = searchParams.get('code');
      const errorParam = searchParams.get('error');
      const errorDescription = searchParams.get('error_description');

      if (errorParam) {
        setError(errorDescription || `Authentication error: ${errorParam}`);
        return;
      }

      if (!code) {
        setError('No authorization code received');
        return;
      }

      try {
        // Exchange the code for tokens via our backend
        const response = await apiPost('/auth/keycloak/callback', {
          code,
          redirectUri: window.location.origin + '/auth/keycloak/callback',
        });

        if (response.success) {
          // Store the tokens
          sessionStorage.setItem('keycloakToken', response.accessToken);
          sessionStorage.setItem('keycloakRefreshToken', response.refreshToken);
          sessionStorage.setItem('userType', 'external_client');

          // Check if user is pending approval
          if (response.pendingApproval) {
            navigate('/pending-approval');
            return;
          }

          // Notify parent component of successful login
          if (onKeycloakLogin) {
            await onKeycloakLogin(response.user);
          }

          // Redirect to client portal
          navigate('/client-portal');
        } else {
          setError(response.message || 'Authentication failed');
        }
      } catch (err) {
        logger.error('Keycloak callback error', { message: err.message, status: err.response?.status });
        if (err.response?.status === 403 && err.response?.data?.pendingApproval) {
          navigate('/pending-approval');
          return;
        }
        setError(err.response?.data?.message || 'Failed to complete authentication');
      }
    };

    handleCallback();
  }, [searchParams, navigate, onKeycloakLogin]);

  const bgSx = {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
    gap: 2,
    background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0f172a 100%)',
    position: 'relative',
    overflow: 'hidden',
    p: 3,
    '&::before': {
      content: '""',
      position: 'absolute',
      inset: 0,
      backgroundImage: `
        url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1200 800'%3E%3Cpath d='M0 400 Q150 320 300 420 T600 360 T900 440 T1200 380' fill='none' stroke='rgba(148,163,184,0.18)' stroke-width='1.5'/%3E%3Cpath d='M0 430 Q180 350 360 460 T720 390 T1080 470 T1200 420' fill='none' stroke='rgba(148,163,184,0.15)' stroke-width='1.2'/%3E%3Cpath d='M0 370 Q120 300 240 380 T480 340 T720 400 T960 350 T1200 370' fill='none' stroke='rgba(148,163,184,0.12)' stroke-width='1'/%3E%3Cpath d='M0 460 Q200 390 400 490 T800 420 T1200 470' fill='none' stroke='rgba(148,163,184,0.15)' stroke-width='1.2'/%3E%3Cpath d='M0 340 Q100 280 200 350 T400 310 T600 360 T800 320 T1000 370 T1200 340' fill='none' stroke='rgba(148,163,184,0.10)' stroke-width='1'/%3E%3Cpath d='M0 490 Q250 440 500 520 T1000 480 T1200 500' fill='none' stroke='rgba(148,163,184,0.12)' stroke-width='1'/%3E%3Cpath d='M0 310 Q300 260 600 330 T1200 300' fill='none' stroke='rgba(148,163,184,0.09)' stroke-width='1'/%3E%3Cpath d='M0 520 Q200 480 400 550 T800 510 T1200 540' fill='none' stroke='rgba(148,163,184,0.09)' stroke-width='1'/%3E%3Cellipse cx='150' cy='200' rx='350' ry='250' fill='rgba(59,130,246,0.08)' /%3E%3Cellipse cx='950' cy='600' rx='400' ry='300' fill='rgba(99,102,241,0.07)' /%3E%3Cellipse cx='600' cy='80' rx='250' ry='180' fill='rgba(148,163,184,0.06)' /%3E%3C/svg%3E")
      `,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      pointerEvents: 'none',
    },
  };

  if (error) {
    return (
      <Box sx={bgSx}>
        <Card sx={{ maxWidth: 400, width: '100%', borderRadius: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.12), 0 20px 60px rgba(0,0,0,0.3)', position: 'relative', zIndex: 1 }}>
          <CardContent sx={{ p: 4, textAlign: 'center' }}>
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
            <Button variant="outlined" href="/login" size="small">
              Return to login
            </Button>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box sx={bgSx}>
      <CircularProgress sx={{ color: 'white', zIndex: 1 }} />
      <Typography sx={{ color: 'white', zIndex: 1 }}>Completing authentication...</Typography>
    </Box>
  );
};

export default KeycloakCallback;
